import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'components/ui';
import { useTheme } from '@mui/material';
import STimePicker from 'components/ui/WorkingHoursPicker/styled/STimePicker';
import type { QuestionInputProps } from '../types/questionnaire';

/**
 * Shared time input component for questionnaires
 * Handles: question_time (when isClockTime(unitCode) is true)
 * Value format: minutes since midnight (number or string)
 */
export const TimeInput: React.FC<QuestionInputProps> = ({
  question,
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Convert minutes since midnight to Date object
  const dateValue = useMemo(() => {
    if (value == null || value === '') {
      return null;
    }

    let minutesSinceMidnight = value;
    if (typeof minutesSinceMidnight === 'string') {
      minutesSinceMidnight = Number(minutesSinceMidnight);
    }

    const hours = Math.floor(minutesSinceMidnight / 60);
    const minutes = minutesSinceMidnight % 60;

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);

    return date;
  }, [value]);

  const handleChange = (date: Date | null) => {
    if (date == null) {
      onChange(null);
    } else {
      const minutesSinceMidnight = date.getHours() * 60 + date.getMinutes();
      onChange(String(minutesSinceMidnight));
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <STimePicker
        use24Hours={true}
        isBig={false}
        value={dateValue}
        onChange={handleChange}
        isError={!!error}
        suffixIcon={<Icon icon={'angle-down'} />}
        minuteStep={1}
        disabled={disabled}
      />
      {error && (
        <p
          style={{
            color: theme.palette.colors.red100,
            width: '100%',
            fontWeight: 400,
            fontSize: 12,
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};
